/** * マップレイヤーコンポーネント */
<template>
  <div class="map-layer-controller">
    <!-- 屋内マップ管理コントローラー -->
    <map-indoor-controller
      v-if="isIndoorAvailable"
      v-bind:mapLayers="mapLayers"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { MapLayerMVTRenderer } from 'ifield-core-components'
import IFieldRasterTileRenderer from '@/js/map-renderer/IFieldRasterTileRenderer.js'
import BackdropRenderer from '@/js/map-renderer/BackdropRenderer.js'
import MapIndoorController from '@/components/map/AppMapIndoorController.vue'
import { FUNCTION_TYPE } from '@/constants.js'
import { useStoreLayerVisible } from '@/composables/UseStoreLayerVisible.js'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

// プレイスをグルーピングするレイヤーの名称
const PLACE_LAYER_ID = 'place'

/**
 * マップレイヤーコンポーネント
 * NOTE:
 *  - マップレイヤーの管理を行うコンポーネント
 *  - マップレイヤー、マップスタイル一覧を取得して、Store に設定
 *  - Deck.gl Layer 用の Renderer の作成
 */
export default {
  name: 'AppMapLayerController',
  components: {
    'map-indoor-controller': MapIndoorController,
  },
  props: {
    /**
     * グローバル(Vuex)のレンダリングstateを使用する場合はtrue
     *  - layerRenderParameters に Vuex map モジュールの値を使用
     *  - 作成した layerRenderers を Vuex map モジュールに保存
     */
    useGlobalRenderState: {
      type: Boolean,
      default: false,
    },
    /**
     * レイヤー描画パラメータ
     */
    layerRenderParameters: {
      type: Array,
      default: null,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    const { targetLayerIds, restoreLayerVisibleFromLocalStorage } =
      useStoreLayerVisible(props, context)
    return {
      handleApiError,
      layerIdsToStoreVisible: targetLayerIds,
      restoreLayerVisibleFromLocalStorage,
    }
  },
  data: function () {
    return {
      mvtRenderer: null,
      iFieldRasterTileRenderer: null,
      backdropRenderer: null,
      createRendererStack: [],
    }
  },
  computed: {
    ...mapGetters('mapLayer', [
      'mapLayers',
      'markerImageStore',
      'labelImageStores',
    ]),
    ...mapGetters('map', ['layerState']),
    /**
     * 屋内機能が利用可能か？
     * @type {Boolean}
     */
    isIndoorAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.INDOOR)
    },
    /**
     * 実効レイヤー描画パラメータ
     * @type {Array.<Object>}
     */
    actualLayerRenderParameters: function () {
      return this.useGlobalRenderState
        ? this.$store.getters['map/layerRenderParameters']
        : this.layerRenderParameters
    },
    /**
     * プレイスのレイヤーID
     * @type {Array.<String>}
     */
    placeLayerIds: function () {
      return this.mapLayers
        .filter((layer) => layer.reference_table_type === +FUNCTION_TYPE.PLACE)
        .map((layer) => `${layer.id}`)
    },
    /**
     * マップレイヤーの最大order
     * @type {Number}
     */
    maxSortOrder: function () {
      if (!this.mapLayers?.length) return 1
      return Math.max(...this.mapLayers.map((layer) => layer.sort_order))
    },
  },
  watch: {
    mapLayers: {
      immediate: true,
      handler: function () {
        this.layerIdsToStoreVisible = [...this.placeLayerIds, PLACE_LAYER_ID]
        this.$_setLayerParameters()
        this.$_setLayerMenuParameters()
      },
    },
    actualLayerRenderParameters: function () {
      this.$_onLayerRenderParametersChanged()
    },
  },
  created: function () {
    if (this.useGlobalRenderState) {
      this.restoreLayerVisibleFromLocalStorage()
      this.$_initLayerParameters()
    }
  },
  mounted: function () {
    this.$_onLayerRenderParametersChanged()
  },
  methods: {
    /**
     * Feature クリックの一括コールバック
     * @param {Object} info deck.gl clickCallback
     */
    $_onClick: function (info) {
      const props = info.object.properties
      this.$log.debug(`[DEBUG] Feature Click`, props)

      // Store に通知
      this.$store.dispatch('mapLayer/onClickFeature', info.object)
    },

    /**
     * レイヤーパラメータの初期設定
     */
    $_initLayerParameters: function () {
      // マップレイヤーのパラメータ設定
      const layerParams = this.$_getMapLayerParameters()

      /**
       * iFieldRasterTile の Style
       */
      if (!this.layerState.style[IFieldRasterTileRenderer.LAYER_ID]) {
        const indoorLevelVisible =
          this.layerState.visible[
            IFieldRasterTileRenderer.SUB_LAYER_ID.INDOOR_LEVEL
          ]
        const mapImageVisible =
          this.layerState.visible[
            IFieldRasterTileRenderer.SUB_LAYER_ID.MAP_IMAGE
          ]
        const rastetTileVisible =
          this.layerState.visible[IFieldRasterTileRenderer.LAYER_ID]

        // サブレイヤー
        layerParams.push({
          id: IFieldRasterTileRenderer.SUB_LAYER_ID.INDOOR_LEVEL,
          visible: indoorLevelVisible === undefined ? true : indoorLevelVisible,
          style: {},
          order: this.maxSortOrder + 1,
        })
        layerParams.push({
          id: IFieldRasterTileRenderer.SUB_LAYER_ID.MAP_IMAGE,
          visible: mapImageVisible === undefined ? true : mapImageVisible,
          style: {},
          order: this.maxSortOrder + 1,
        })
        // 親レイヤー
        layerParams.push({
          id: IFieldRasterTileRenderer.LAYER_ID,
          visible: rastetTileVisible === undefined ? true : rastetTileVisible,
          style: {},
          order: this.maxSortOrder + 1,
          subLayers: [...Object.values(IFieldRasterTileRenderer.SUB_LAYER_ID)],
          callback: {
            onFetchError: (error) => this.handleApiError(error, null),
          },
        })
      }

      /**
       * backdropRendererのStyle
       */
      if (!this.layerState.style[BackdropRenderer.LAYER_ID]) {
        layerParams.push({
          id: BackdropRenderer.LAYER_ID,
          style: {
            pickable: false,
          },
          visible: false,
          order: this.maxSortOrder + 2,
        })
      }

      this.$store.dispatch('map/setLayerParameter', layerParams)
    },

    /**
     * マップレイヤーのパラメータ取得
     */
    $_getMapLayerParameters: function () {
      let layerParams = this.mapLayers.map((layer, index) => {
        const visible = this.layerState.visible[`${layer.id}`]
        return {
          id: `${layer.id}`,
          style: layer,
          order: index,
          visible:
            visible === undefined
              ? {
                  geometry: true,
                  label: true,
                }
              : visible,
          callback: {
            onClick: this.$_onClick,
          },
        }
      })
      const mvtVisible = this.layerState.visible[MapLayerMVTRenderer.LAYER_ID()]
      layerParams.push({
        id: MapLayerMVTRenderer.LAYER_ID(),
        style: {},
        order: 1,
        visible: mvtVisible === undefined ? true : mvtVisible,
        subLayers: this.mapLayers.map((layer) => `${layer.id}`),
        depthTest: false,
        depthWriteEnabled: false, // v9推奨: 透明部分が深度バッファに書き込まれるのを防ぐ
        blend: true,
        callback: {
          onFetchError: (error) => this.handleApiError(error, null),
        },
      })

      return layerParams
    },

    /**
     * レイヤーパラメータの設定
     */
    $_setLayerParameters: function () {
      // マップレイヤーのパラメータ設定
      let layerParams = this.$_getMapLayerParameters()

      // iFieldRasterTile のパラメータ設定
      // マップレイヤーが増減している可能性があるのでorderだけ更新する
      layerParams = [
        ...layerParams,
        // サブレイヤー
        {
          id: IFieldRasterTileRenderer.SUB_LAYER_ID.INDOOR_LEVEL,
          order: this.maxSortOrder + 1,
        },
        {
          id: IFieldRasterTileRenderer.SUB_LAYER_ID.MAP_IMAGE,
          order: this.maxSortOrder + 1,
        },
        // 親レイヤー
        {
          id: IFieldRasterTileRenderer.LAYER_ID,
          order: this.maxSortOrder + 1,
        },
      ]

      // backdrop のパラメータ設定
      // マップレイヤーが増減している可能性があるのでorderだけ更新する
      layerParams.push({
        id: BackdropRenderer.LAYER_ID,
        order: this.maxSortOrder + 2,
      })

      this.$store.dispatch('map/setLayerParameter', layerParams)
    },

    /**
     * レイヤーメニュー設定
     */
    $_setLayerMenuParameters: function () {
      const layerMenuParams = this.mapLayers.map((layer) => ({
        id: `${layer.id}`,
        name: layer.name,
        color: layer.style.line_color,
        labeled: true,
      }))
      // プレイスのグルーピング用
      layerMenuParams.push({
        id: PLACE_LAYER_ID,
        name: this.$t('appMapLayerController.layer.place'),
        color: '#2a8e5c',
        subLayers: this.mapLayers
          .filter(
            (layer) => layer.reference_table_type === +FUNCTION_TYPE.PLACE
          )
          .map((layer) => `${layer.id}`),
      })
      // マップイメージ
      layerMenuParams.push({
        id: IFieldRasterTileRenderer.SUB_LAYER_ID.MAP_IMAGE,
        name: this.isIndoorAvailable
          ? this.$t('appMapLayerController.layer.mapImageOutdoor')
          : this.$t('appMapLayerController.layer.mapImage'),
      })
      this.$store.dispatch(
        'map/layerMenu/setLayerMenuParameter',
        layerMenuParams
      )
    },

    /**
     * レイヤー描画パラメータ変更時処理
     */
    $_onLayerRenderParametersChanged: async function () {
      let renderers
      try {
        // createLayerRendererの実行時間に差があり、古いパラメータを内包したrendererが後に返ってくることがあるため、
        // 当関数のトリガー順にPromiseをスタックに貯めておき、常に最後にトリガーされたパラメータを内包したrendererを使用できるようにする
        this.createRendererStack.push(this.$_createLayerRenderers())
        const results = await Promise.all(this.createRendererStack)
        renderers = results[results.length - 1]
      } catch (error) {
        this.$log.error(error)
        return
      }

      if (this.useGlobalRenderState) {
        this.$store.dispatch('map/setLayerRenderers', renderers)
      }

      /**
       * レンダラー作成完了イベント
       * @param {Array.<Object>}
       */
      this.$emit('map-layer-renderers-created', renderers)

      // スタックのクリア
      this.createRendererStack = []
    },

    /**
     * レイヤーレンダラー生成
     * @returns {Array.<Object>} レンダラー配列
     */
    $_createLayerRenderers: async function () {
      // レンダラーファクトリー作成
      if (!this.mvtRenderer) {
        this.mvtRenderer = new MapLayerMVTRenderer({
          markerImageStore: this.markerImageStore,
          labelImageStores: this.labelImageStores,
        })
      }
      if (!this.iFieldRasterTileRenderer) {
        this.iFieldRasterTileRenderer = new IFieldRasterTileRenderer()
      }
      if (!this.backdropRenderer) {
        this.backdropRenderer = new BackdropRenderer()
      }
      try {
        // マップレイヤー
        const renderers = await Promise.all(
          this.actualLayerRenderParameters.map((param) => {
            switch (param.id) {
              case MapLayerMVTRenderer.LAYER_ID():
                return this.mvtRenderer.createRenderer(param)
              case IFieldRasterTileRenderer.LAYER_ID:
                return this.iFieldRasterTileRenderer.createRenderer(param)
              case BackdropRenderer.LAYER_ID:
                return this.backdropRenderer.createRenderer(param)
              default:
                return
            }
          })
        )

        // FIXME: 配列に undefined が入るケースが filter する
        return renderers.filter((v) => v)
      } catch (e) {
        this.$log.warn('[DEBUG] createLayerRenderers Exception', e)
        throw e
      }
    },
  },
}
</script>
